import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  Check,
  X,
  Crown,
  Edit3,
  Eye,
  Loader2,
  Circle,
  Trash2,
  Bell,
  Copy
} from 'lucide-react';

const CollaborationPanel = ({ contractId, isOwner }) => {
  const { api, user } = useAuth();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [myRole, setMyRole] = useState(null);

  useEffect(() => {
    fetchCollaborators();
    fetchActiveUsers();
    
    // Poll for active users every 30 seconds
    const interval = setInterval(fetchActiveUsers, 30000);
    return () => clearInterval(interval);
  }, [contractId]);

  const fetchCollaborators = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${contractId}/collaborators`);
      setCollaborators(response.data.collaborators || []);
      setOwnerInfo(response.data.owner);
      setMyRole(response.data.your_role);
    } catch (error) {
      console.error('Error fetching collaborators:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/presence`);
      setActiveUsers(response.data.active_users || []);
    } catch (error) {
      // Silently fail for presence
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      await api.post(`/contracts/${contractId}/collaborators/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole
      });
      toast.success(`Invitación enviada a ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar invitación');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId, username) => {
    if (!window.confirm(`¿Eliminar a ${username} como colaborador?`)) return;

    try {
      await api.delete(`/contracts/${contractId}/collaborators/${userId}`);
      toast.success('Colaborador eliminado');
      fetchCollaborators();
    } catch (error) {
      toast.error('Error al eliminar colaborador');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-400" />;
      case 'editor': return <Edit3 className="w-4 h-4 text-blue-400" />;
      case 'viewer': return <Eye className="w-4 h-4 text-slate-400" />;
      default: return null;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner': return 'Propietario';
      case 'editor': return 'Editor';
      case 'viewer': return 'Lector';
      default: return role;
    }
  };

  const isUserActive = (userId) => {
    return activeUsers.some(u => u.user_id === userId);
  };

  const getUserColor = (userId) => {
    const activeUser = activeUsers.find(u => u.user_id === userId);
    return activeUser?.color || '#6B7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="collaboration-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Colaboradores
        </h3>
        {myRole === 'owner' && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-500 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Invitar
          </button>
        )}
      </div>

      {/* Active Users Indicator */}
      {activeUsers.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex -space-x-2">
            {activeUsers.slice(0, 5).map((user, i) => (
              <div
                key={user.user_id}
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white border-2 border-card"
                style={{ backgroundColor: user.color, zIndex: 5 - i }}
                title={user.username}
              >
                {user.username?.charAt(0).toUpperCase() || '?'}
              </div>
            ))}
          </div>
          <span className="text-sm text-green-400">
            {activeUsers.length} {activeUsers.length === 1 ? 'persona' : 'personas'} editando ahora
          </span>
        </div>
      )}

      {/* Owner */}
      {ownerInfo && (
        <div className="p-3 bg-slate-800/50 rounded-lg">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium relative"
              style={{ backgroundColor: getUserColor(ownerInfo.id) }}
            >
              {ownerInfo.username?.charAt(0).toUpperCase() || '?'}
              {isUserActive(ownerInfo.id) && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{ownerInfo.username}</span>
                <Crown className="w-4 h-4 text-yellow-400" />
              </div>
              <span className="text-sm text-slate-400">{ownerInfo.email}</span>
            </div>
            <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
              Propietario
            </span>
          </div>
        </div>
      )}

      {/* Collaborators List */}
      {collaborators.length > 0 ? (
        <div className="space-y-2">
          {collaborators.map((collab) => (
            <div key={collab.id} className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium relative"
                  style={{ backgroundColor: getUserColor(collab.user_id) }}
                >
                  {collab.username?.charAt(0).toUpperCase() || collab.user_email?.charAt(0).toUpperCase()}
                  {isUserActive(collab.user_id) && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{collab.username || collab.user_email}</span>
                    {getRoleIcon(collab.role)}
                  </div>
                  <span className="text-sm text-slate-400">{collab.user_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    collab.role === 'editor' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'
                  }`}>
                    {getRoleLabel(collab.role)}
                  </span>
                  {myRole === 'owner' && (
                    <button
                      onClick={() => handleRemove(collab.user_id, collab.username || collab.user_email)}
                      className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                      title="Eliminar colaborador"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay colaboradores aún</p>
          {myRole === 'owner' && (
            <p className="text-sm mt-1">Invita a alguien para trabajar juntos</p>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-400" />
                Invitar Colaborador
              </h3>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colaborador@ejemplo.com"
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Rol
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setInviteRole('editor')}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      inviteRole === 'editor' 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-border hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Edit3 className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Editor</span>
                    </div>
                    <p className="text-xs text-slate-400">Puede editar el contrato</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteRole('viewer')}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      inviteRole === 'viewer' 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-border hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">Lector</span>
                    </div>
                    <p className="text-xs text-slate-400">Solo puede ver</p>
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 py-2.5 border border-slate-600 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {inviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Enviar Invitación
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Invitation Badge Component for Dashboard
export const InvitationBadge = () => {
  const { api } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/invitations');
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (invitationId, accept) => {
    try {
      await api.post(`/invitations/${invitationId}/respond`, { accept });
      toast.success(accept ? 'Invitación aceptada' : 'Invitación rechazada');
      fetchInvitations();
    } catch (error) {
      toast.error('Error al responder invitación');
    }
  };

  if (loading || invitations.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors"
        data-testid="invitation-badge"
      >
        <Bell className="w-5 h-5" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
          {invitations.length}
        </span>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Invitaciones Pendientes</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {invitations.map((inv) => (
                <div key={inv.id} className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="font-medium mb-1">{inv.contract_title}</p>
                  <p className="text-sm text-slate-400 mb-3">
                    Te invitaron como <span className="text-blue-400">{inv.role === 'editor' ? 'Editor' : 'Lector'}</span>
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResponse(inv.id, true)}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 text-sm flex items-center justify-center gap-1"
                    >
                      <Check className="w-4 h-4" />
                      Aceptar
                    </button>
                    <button
                      onClick={() => handleResponse(inv.id, false)}
                      className="flex-1 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center justify-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Rechazar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CollaborationPanel;
